import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAuthHeader } from "../../utils/auth";
import { isPdfUploadFile } from '../../utils/eContractUpload';
import {
    E_CONTRACT_FREE_LIMIT_CODE,
    buildReachedEContractQuota,
    canOpenOwnBilling,
    isEContractCreationBlocked,
    normalizeEContractQuota,
} from '../../utils/eContractQuota';
import PdfSignatureEditor from './PdfSignatureEditor';

const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const EContractTab = ({ customerId, clientId, user, contacts }) => {
    const [currentStep, setCurrentStep] = useState('upload'); // upload | editor | send
    const [isUploading, setIsUploading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [resendingId, setResendingId] = useState(null);
    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [quota, setQuota] = useState(null);
    const resendRequestsRef = useRef(new Map());
    const quotaRequestRef = useRef(0);
    
    const [uploadedData, setUploadedData] = useState({
        requestId: null,
        pdfData: null,
        title: ''
    });

    const [selectedContactId, setSelectedContactId] = useState("");
    const isCreationBlocked = isEContractCreationBlocked(quota);
    const showFreeQuota = Boolean(quota?.enforcement_enabled && !quota?.is_paid);
    const canOpenBilling = canOpenOwnBilling(user, clientId);

    // --- 1. 履歴データの取得 ---
    const fetchHistory = useCallback(async () => {
        setIsLoadingHistory(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/contracts/history/${customerId}?client_id=${clientId}`, {
                headers: getAuthHeader()
            });
            const data = await res.json();
            if (data.success) {
                setHistory(data.contracts || []);
            }
        } catch (err) {
            console.error("履歴取得エラー:", err);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [customerId, clientId]);

    // 履歴とは独立して利用枠を取得する。取得失敗時はUIで作成を止めず、作成APIを正本とする。
    const fetchQuota = useCallback(async () => {
        const requestId = quotaRequestRef.current + 1;
        quotaRequestRef.current = requestId;
        setQuota(null);
        if (!clientId) return;

        try {
            const res = await fetch(`${API_URL}/api/marketing/contracts/usage?client_id=${encodeURIComponent(clientId)}`, {
                headers: getAuthHeader()
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'quota_fetch_failed');

            const nextQuota = normalizeEContractQuota(data);
            if (!nextQuota) throw new Error('invalid_quota_response');
            if (quotaRequestRef.current === requestId) setQuota(nextQuota);
        } catch (err) {
            if (quotaRequestRef.current === requestId) {
                setQuota(null);
                console.error("電子契約の利用状況取得エラー:", err);
            }
        }
    }, [clientId]);

    // ★ 追加：編集（下書き再開）
    const handleEdit = async (id) => {
        try {
            const res = await fetch(`${API_URL}/api/public/contracts/detail/${id}`, {
                headers: getAuthHeader()
            });
            const data = await res.json();
            if (data.success) {
                setUploadedData({
                    requestId: id,
                    pdfData: data.pdfData,
                    title: data.contract.title
                });
                setCurrentStep('editor');
            }
        } catch (e) { alert("データの取得に失敗しました"); }
    };

    // ★ 追加：削除処理
    const handleDelete = async (id) => {
        if (!window.confirm("この契約依頼を完全に削除しますか？")) return;
        try {
            const res = await fetch(`${API_URL}/api/marketing/contracts/${id}`, {
                method: 'DELETE',
                headers: getAuthHeader(true)
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) throw new Error(data.error || "削除に失敗しました");
            await fetchHistory();
        } catch (e) { alert(e.message || "削除に失敗しました"); }
    };

    const handleResend = async (id) => {
        const existingRequest = resendRequestsRef.current.get(id);
        if (existingRequest?.inFlight) return;
        if (!window.confirm("署名依頼を再送しますか？現在の署名URLはすぐに無効になります。")) return;
        const idempotencyKey = existingRequest?.idempotencyKey || window.crypto.randomUUID();
        resendRequestsRef.current.set(id, { idempotencyKey, inFlight: true });
        setResendingId(id);
        try {
            const res = await fetch(`${API_URL}/api/marketing/contracts/${id}/resend`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Idempotency-Key': idempotencyKey,
                    ...getAuthHeader(),
                },
                body: JSON.stringify({}),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'resend_failed');
            await fetchHistory();
            resendRequestsRef.current.delete(id);
            alert("再送を受け付けました。旧URLは無効化されています。");
        } catch (error) {
            resendRequestsRef.current.set(id, { idempotencyKey, inFlight: false });
            alert(error.message === 'resend_failed' ? "再送に失敗しました。" : error.message);
        } finally {
            setResendingId(null);
        }
    };

    useEffect(() => {
        if (currentStep === 'upload') {
            fetchHistory();
        }
    }, [currentStep, fetchHistory]);

    useEffect(() => {
        if (currentStep === 'upload') {
            fetchQuota();
        }
    }, [currentStep, fetchQuota]);

    // --- 2. ファイルアップロード処理 ---
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!isPdfUploadFile(file)) {
            alert('PDFファイルを選択してください。');
            return;
        }

        const title = prompt("書類のタイトルを入力してください", "業務委託契約書");
        if (!title) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('client_id', clientId);
        formData.append('customer_id', customerId);
        formData.append('title', title);

        try {
            const res = await fetch(`${API_URL}/api/marketing/contracts/upload`, {
                method: 'POST',
                headers: getAuthHeader(), 
                body: formData
            });
            const data = await res.json().catch(() => ({}));

            if (res.ok && data.success) {
                setUploadedData({
                    requestId: data.requestId,
                    pdfData: data.pdfData,
                    title: title
                });
                setCurrentStep('editor');
            } else if (res.status === 403 && data.code === E_CONTRACT_FREE_LIMIT_CODE) {
                quotaRequestRef.current += 1;
                setQuota(previousQuota => buildReachedEContractQuota(data, previousQuota));
                e.target.value = '';
                alert('無料プランの電子契約は3件までです。既存の契約書は引き続き利用できます。');
            } else {
                throw new Error(data.error || 'アップロードに失敗しました。');
            }
        } catch (err) {
            alert(err.message || 'アップロードに失敗しました。');
        } finally {
            setIsUploading(false);
        }
    };

    // --- 3. 配置保存 ＆ 送信処理 ---
    const handleSaveFields = async (fields) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/contracts/${uploadedData.requestId}/fields`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ fields })
            });
            if (res.ok) setCurrentStep('send');
            else alert("配置の保存に失敗しました。");
        } catch (err) { alert("通信エラーが発生しました。"); }
    };

    const handleFinalSend = async () => {
        if (!selectedContactId) return alert("送信先の担当者を選択してください。");
        setIsSending(true);
        try {
            const res = await fetch(`${API_URL}/api/marketing/contracts/${uploadedData.requestId}/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId, contact_id: selectedContactId })
            });
            const data = await res.json();
            if (data.success) {
                alert("契約依頼メールの送信を受け付けました！");
                setCurrentStep('upload');
                setUploadedData({ requestId: null, pdfData: null, title: '' });
                setSelectedContactId("");
            }
        } catch (err) { alert("送信に失敗しました。"); }
        finally { setIsSending(false); }
    };

    // --- 4. ダウンロード ＆ URLコピー ---
    const handleDownload = async (id) => {
        try {
            const response = await fetch(`${API_URL}/api/marketing/contracts/${id}/download`, {
                headers: getAuthHeader(),
            });
            if (!response.ok) throw new Error('download_failed');
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `contract-${id}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
        } catch (_error) {
            alert("書類のダウンロードに失敗しました。");
        }
    };

    const copySignUrl = (viewToken) => {
        const url = `${window.location.origin}/app/sign#${viewToken}`;
        navigator.clipboard.writeText(url);
        alert("署名用URLをコピーしました！");
    };

    // ステータス表示用バッジ
    const StatusBadge = ({ status }) => {
        const styles = {
            draft: "bg-slate-100 text-slate-600",
            sent: "bg-blue-100 text-blue-600",
            viewed: "bg-indigo-100 text-indigo-600",
            signed: "bg-green-100 text-green-600 font-bold",
            void: "bg-red-100 text-red-600"
        };
        const labels = { draft: "下書き", sent: "送信済", viewed: "確認中", signed: "締結完了", void: "無効" };
        return <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider ${styles[status] || styles.draft}`}>{labels[status] || status}</span>;
    };

    return (
        <div className="space-y-8">
            {currentStep === 'upload' && (
                <>
                    {showFreeQuota && (
                        <div
                            className={`flex flex-col gap-4 rounded-2xl border p-5 sm:flex-row sm:items-center sm:justify-between ${isCreationBlocked ? 'border-amber-200 bg-amber-50' : 'border-blue-100 bg-blue-50'}`}
                            role={isCreationBlocked ? 'alert' : 'status'}
                        >
                            <div>
                                <p className={`text-sm font-black ${isCreationBlocked ? 'text-amber-900' : 'text-blue-900'}`}>
                                    無料プランの電子契約
                                </p>
                                <p className={`mt-1 text-sm font-bold ${isCreationBlocked ? 'text-amber-800' : 'text-blue-700'}`}>
                                    {quota.current} / {quota.limit}件利用
                                    {!isCreationBlocked && `（残り${quota.remaining}件）`}
                                </p>
                                <p className={`mt-2 text-xs font-medium leading-5 ${isCreationBlocked ? 'text-amber-800' : 'text-blue-700'}`}>
                                    {isCreationBlocked
                                        ? '上限に達したため、新しい契約書は作成できません。既存の契約書は引き続き利用できます。'
                                        : '無料プランでは電子契約を3件まで作成できます。'}
                                </p>
                            </div>
                            {isCreationBlocked && (
                                canOpenBilling ? (
                                    <Link
                                        to="/account"
                                        className="inline-flex w-full shrink-0 items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-sm font-black text-white transition-colors hover:bg-blue-700 sm:w-auto"
                                    >
                                        有料プランを確認する
                                    </Link>
                                ) : (
                                    <p className="w-full rounded-xl border border-amber-200 bg-white px-4 py-3 text-xs font-bold leading-5 text-amber-900 sm:max-w-xs">
                                        継続利用については、ご契約先の担当者までお問い合わせください。
                                    </p>
                                )
                            )}
                        </div>
                    )}

                    {/* アップロードエリア */}
                    <div className={`bg-white p-6 sm:p-10 rounded-3xl sm:rounded-[2.5rem] shadow-sm border text-center animate-in fade-in duration-500 ${isCreationBlocked ? 'border-slate-200' : 'border-blue-50'}`}>
                        <input type="file" id="contract-upload" aria-label="契約書PDFを選択" className="hidden" accept=".pdf" onChange={handleFileUpload} disabled={isUploading || isCreationBlocked} />
                        <label htmlFor="contract-upload" aria-disabled={isCreationBlocked} className={isCreationBlocked ? 'cursor-not-allowed group' : 'cursor-pointer group'}>
                            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 transition-transform duration-300 ${isCreationBlocked ? 'bg-slate-100 text-slate-400' : 'bg-blue-50 text-blue-600 group-hover:scale-110'}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                            </div>
                            <h3 className="text-xl font-black text-slate-800">
                                {isUploading ? 'アップロード中...' : isCreationBlocked ? '新しい契約書の作成上限に達しました' : '新しい契約書を送る'}
                            </h3>
                            <p className="text-slate-400 text-sm mt-2 font-medium">
                                {isCreationBlocked ? '既存の契約書は下の履歴から操作できます' : 'クリックしてPDFを選択'}
                            </p>
                        </label>
                    </div>

                    {/* 履歴リスト */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center px-4">
                            <h4 className="text-sm font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                送信履歴
                            </h4>
                            <button onClick={fetchHistory} className="text-xs text-blue-600 font-bold hover:underline">更新</button>
                        </div>

                        <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-x-auto">
                            <table className="w-full min-w-[680px] text-left text-sm">
                                <thead className="bg-slate-50 text-slate-400 font-bold text-[10px] uppercase tracking-widest">
                                    <tr>
                                        <th className="p-5">書類タイトル / 送信日時</th>
                                        <th className="p-5">ステータス</th>
                                        <th className="p-5 text-right">アクション</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {isLoadingHistory ? (
                                        <tr><td colSpan="3" className="p-10 text-center text-slate-300 font-bold animate-pulse">読み込み中...</td></tr>
                                    ) : history.length === 0 ? (
                                        <tr><td colSpan="3" className="p-10 text-center text-slate-300 italic font-medium">履歴がありません</td></tr>
                                    ) : history.map(item => (
                                        <tr key={item.id} className="hover:bg-slate-50/50 transition-colors group">
                                            <td className="p-5">
                                                <p className="font-black text-slate-700">{item.title}</p>
                                                <p className="text-[10px] text-slate-400 mt-1 font-bold">{new Date(item.created_at).toLocaleString('ja-JP')}</p>
                                                {(item.status === 'sent' || item.status === 'viewed') && (
                                                    <p className="text-[10px] text-slate-400 mt-1 font-bold">
                                                        {item.expires_at && new Date(item.expires_at).getTime() > Date.now()
                                                            ? `URL期限: ${new Date(item.expires_at).toLocaleString('ja-JP')}`
                                                            : 'URL期限切れ（再送が必要）'}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="p-5"><StatusBadge status={item.status} /></td>
                                            <td className="p-5 text-right space-x-2">
                                                {/* ★ 編集ボタン（下書きのみ） */}
                                                {item.status === 'draft' && (
                                                    <button onClick={() => handleEdit(item.id)} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-black hover:bg-blue-100">編集</button>
                                                )}
                                                
                                                {/* ★ 削除ボタン（締結済み以外のみ表示） */}
                                                {item.status !== 'signed' && (
                                                    <button onClick={() => handleDelete(item.id)} className="p-1.5 text-slate-300 hover:text-red-500 transition-colors">
                                                        <TrashIcon />
                                                    </button>
                                                )}
                                                
                                                {(item.status === 'sent' || item.status === 'viewed') && item.view_token && (
                                                    <button onClick={() => copySignUrl(item.view_token)} className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black hover:bg-indigo-100">URLコピー</button>
                                                )}
                                                {(item.status === 'sent' || item.status === 'viewed') && (
                                                    <button
                                                        onClick={() => handleResend(item.id)}
                                                        disabled={resendingId === item.id}
                                                        className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-black hover:bg-amber-100 disabled:opacity-50"
                                                    >
                                                        {resendingId === item.id ? '再送受付中...' : '再送'}
                                                    </button>
                                                )}
                                                <button onClick={() => handleDownload(item.id)} className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black hover:bg-black transition-all">
                                                    {item.status === 'signed' ? '完了書類DL' : '原本DL'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* エディタ ＆ 送信ステップ (既存通り) */}
            {currentStep === 'editor' && uploadedData.pdfData && (
                <PdfSignatureEditor pdfData={uploadedData.pdfData} onSave={handleSaveFields} onCancel={() => setCurrentStep('upload')} />
            )}

            {currentStep === 'send' && (
                <div className="bg-white p-6 sm:p-10 rounded-3xl sm:rounded-[3rem] shadow-2xl border border-blue-50 max-w-2xl mx-auto animate-in slide-in-from-bottom-8 duration-500">
                    <div className="text-center mb-8 sm:mb-10">
                        <div className="w-20 h-20 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        </div>
                        <h3 className="text-2xl font-black text-slate-800">送信先の選択</h3>
                        <p className="text-slate-400 mt-2 font-medium">「{uploadedData.title}」の署名依頼を送信します。</p>
                    </div>

                    <div className="space-y-8">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-2">宛先担当者</label>
                            <select className="w-full p-5 bg-slate-50 border-2 border-slate-100 rounded-3xl font-black text-slate-700 outline-none focus:border-blue-500 focus:bg-white transition-all shadow-inner" value={selectedContactId} onChange={(e) => setSelectedContactId(e.target.value)}>
                                <option value="">担当者を選択...</option>
                                {contacts && contacts.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
                            </select>
                        </div>

                        <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:gap-4">
                            <button onClick={() => setCurrentStep('editor')} className="w-full py-4 sm:flex-1 sm:py-5 text-slate-400 font-black hover:text-slate-600 transition-colors">配置を直す</button>
                            <button onClick={handleFinalSend} disabled={isSending || !selectedContactId} className="w-full sm:flex-[2] py-4 sm:py-5 bg-blue-600 text-white rounded-full font-black shadow-2xl shadow-blue-100 hover:bg-blue-700 disabled:bg-slate-200 transition-all flex items-center justify-center gap-3">
                                {isSending ? '送信中...' : 'メールを送信して依頼する'}
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EContractTab;
